import { css } from 'emotion';
import React, { useState } from 'react';

import theme from '@vromo/ui/components/theme';

import { observer } from 'mobx-react-lite';

import { TaskBlueprint } from '@vromo/models';
import FontAwesome from '@vromo/ui/components/FontAwesome';

import {
    closeButton,
    Constraint,
    TaskContainer
} from '@vromo/dashboard/src/blueprints/BlueprintStyles';
import DisplayTitle from '@vromo/dashboard/src/blueprints/DisplayTitle';

type Props = {
    index: number;
    taskTemplate: TaskBlueprint;
    selectTask: () => void;
    removeTask: () => void;
};

const TaskTag = observer(function TaskTag({
    color,
    icon,
    label
}: {
    color: string;
    icon: string;
    label: string;
}) {
    return (
        <Constraint color={color}>
            <FontAwesome icon={icon} style={{ marginRight: 5 }} weight="solid" />
            {label}
        </Constraint>
    );
});

export default observer(function BlueprintTask({
    index,
    taskTemplate,
    selectTask,
    removeTask
}: Props) {
    const hasPhotoConstraint = taskTemplate.constraints.has('photo');
    const hasSignatureConstraint = taskTemplate.constraints.has('signature');
    const autoCompletion = !taskTemplate.constraints.has('manual');

    const [title] = useState(() => (taskTemplate.zone && taskTemplate.zone.name) ?? '');
    const [source] = useState(() => taskTemplate.source);

    return (
        <TaskContainer onClick={selectTask}>
            <div
                className={css`
                    display: flex;
                    flex: 1;
                    justify-content: space-between;
                `}>
                <div
                    className={css`
                        font-weight: bold;
                        font-size: 1rem;
                        display: flex;
                        flex: 1;
                        align-items: center;
                    `}>
                    <DisplayTitle title={title} index={index} source={source} />
                </div>
                <FontAwesome
                    icon="times"
                    className={closeButton}
                    onClick={(e: { preventDefault: () => void; stopPropagation: () => void }) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeTask();
                    }}
                />
            </div>
            <div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontWeight: 'bold',
                        paddingTop: '5px'
                    }}>
                    <div style={{ flex: 2 }}>Input Task:</div>
                    <div style={{ display: 'flex', flex: 4 }}>
                        {source === 0 ? 'Pickup' : 'Dropoff'}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                    <div style={{ flex: 2 }}>Completion Method:</div>
                    <div style={{ display: 'flex', flex: 4 }}>
                        {autoCompletion ? (
                            <TaskTag
                                color={theme.tertiary}
                                icon="location"
                                label="Using Geofence"
                            />
                        ) : (
                            <TaskTag
                                color={theme.tertiary}
                                icon="hand-pointer"
                                label="Manual Swipe"
                            />
                        )}
                    </div>
                </div>
                {taskTemplate.offset && taskTemplate.offset > 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                        <div style={{ flex: 2 }}>Deadline:</div>
                        <div style={{ display: 'flex', flex: 4 }}>
                            <TaskTag
                                color={theme.danger}
                                icon="clock"
                                label={`+${(taskTemplate.offset / 60000).toFixed(0)} mins`}
                            />
                        </div>
                    </div>
                ) : null}

                {hasPhotoConstraint || hasSignatureConstraint ? (
                    <div style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                        <div style={{ flex: 2 }}>Requirements:</div>
                        <div style={{ display: 'flex', flex: 4 }}>
                            {hasPhotoConstraint && (
                                <TaskTag color={theme.primary} icon="camera" label="Photo Upload" />
                            )}
                            {hasSignatureConstraint && (
                                <TaskTag color={theme.primary} icon="signature" label="Signature" />
                            )}
                        </div>
                    </div>
                ) : null}
            </div>
        </TaskContainer>
    );
});
